import type { FinancialProductStrategy } from '@generated/graphql';
import type { FinancialProduct } from '@interfaces/financialProduct';
import type { TranslatedString } from '@interfaces/shared';
import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import type { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import type { AssetClassTargetAllocation } from '../addModelPortfoliosIPSAndAssetClasses/assetClassTargetAllocation.types';
import type { ModelPortfolioGroupBy } from './modelPortfolioGroupBy';

export type CreateModelPortfolioProps = {
  options: CreateModelPortfolioOptions;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: {
    workflow?: {
      id: string;
      name: string;
    };
    id: string;
    objectId: string;
    objectType: string;
  };
  onBack: () => void;
};

export enum SegmentType {
  CHOOSE_PORTFOLIO_TYPE = 'CHOOSE_PORTFOLIO_TYPE',
  SELECT_PORTFOLIO_TEMPLATE = 'SELECT_PORTFOLIO_TEMPLATE',
  CREATE_PORTFOLIO_TEMPLATE = 'CREATE_PORTFOLIO_TEMPLATE',
}

export type CreateModelPortfolioOptions = {
  choosePortfolioType: {
    enabled: boolean;
    required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
    canGoBack: boolean;
    title: TranslatedString;
    subtitle: TranslatedString;
    options: {
      label: TranslatedString;
      description: TranslatedString;
      value: string;
    }[];
  };
  selectTemplate: {
    enabled: boolean;
    required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
    canGoBack: boolean;
    label: TranslatedString;
    description: TranslatedString;
    templateSelector: {
      enabled: boolean;
      required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      label: TranslatedString;
    };
    assetClassAllocation: {
      enabled: boolean;
      required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      label: TranslatedString;
    };
  };
  createTemplate: {
    enabled: boolean;
    canGoBack: boolean;
    required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
    label: TranslatedString;
    description: TranslatedString;
    templateName: {
      enabled: boolean;
      required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      label: TranslatedString;
    };
    templateDescription: {
      enabled: boolean;
      required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      label: TranslatedString;
    };
    assetClassAllocationEditor: {
      enabled: boolean;
      required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      label: TranslatedString;
      assetClassAllocation: {
        enabled: boolean;
        required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
        label: TranslatedString;
      };
      targetPercentage: {
        enabled: boolean;
        required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
        label: TranslatedString;
      };
    };
    buttonSaveForLater: {
      enabled: boolean;
      label: TranslatedString;
    };
  };
};

export interface ModelPortfolioData extends FinancialProduct {
  organizationId: string;
  creationType?: 'FROM_TEMPLATE' | 'FROM_SCRATCH';
  themeId?: string;
  riskProfileName?: TranslatedString;
  hasUnsavedChanges?: boolean;
  securities?: Security[];
  originalValues?: ModelPortfolioData;
  targetAllocations?: AssetClassTargetAllocation[];
  groupBy?: ModelPortfolioGroupBy;
  strategy?: FinancialProductStrategy;
  templateCreationInProgress?: boolean;
}
